/**
 * "Explain the Changes" View Component (F-08)
 *
 * Calls the LLM to explain each change between original and suggested text,
 * displaying results in an expandable accordion.
 */

import React, { useState, useCallback } from 'react';
import { aiEditPrompts } from '../../prompts';
import { callOpenAIStream } from '../../utils/openaiHelper';
import { useRequestManager } from '../../utils/requestManager';
import { trackEvent, trackTiming } from '../../utils/telemetry';
import type { ChangeExplanation } from './types';

const isValidChangeExplanation = (v: unknown): v is ChangeExplanation => {
    if (!v || typeof v !== 'object') return false;
    const o = v as Record<string, unknown>;
    return typeof o.change === 'string' && o.change.trim().length > 0
        && typeof o.reason === 'string' && o.reason.trim().length > 0;
};

interface ExplainChangesViewProps {
    original: string;
    suggested: string;
    cache: React.MutableRefObject<Map<string, ChangeExplanation[]>>;
}

const ExplainChangesView: React.FC<ExplainChangesViewProps> = ({ original, suggested, cache }) => {
    const [explanations, setExplanations] = useState<ChangeExplanation[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [isSectionOpen, setIsSectionOpen] = useState(false);
    const requestManager = useRequestManager();

    const fetchExplanations = useCallback(async () => {
        if (!requestManager.canMakeRequest()) return;
        if (!aiEditPrompts.explainChangesTemplate) return;

        // Check cache before making an API call
        const cacheKey = `${original}|${suggested}`;
        const cached = cache.current.get(cacheKey);
        if (cached) {
            setExplanations(cached);
            setHasLoaded(true);
            if (cached.length > 0) setExpandedIndex(0);
            return;
        }

        requestManager.startRequest();
        setIsLoading(true);
        setError(null);
        setExplanations([]);
        const startTime = Date.now();

        try {
            const prompt = aiEditPrompts.explainChangesTemplate(original, suggested);

            let fullResponse = '';
            const stream = callOpenAIStream({
                signal: requestManager.getSignal(),
                response_format: { type: 'json_object' },
                messages: [
                    { role: 'system', content: 'You are a writing expert. Explain editing changes clearly and concisely.' },
                    { role: 'user', content: prompt },
                ],
            });

            for await (const chunk of stream) {
                fullResponse += chunk;
            }

            // Parse and validate the response
            let parsed: ChangeExplanation[];
            try {
                const json = JSON.parse(fullResponse);
                let raw: unknown[];
                if (Array.isArray(json)) {
                    raw = json;
                } else if (json && Array.isArray(json.explanations)) {
                    raw = json.explanations;
                } else {
                    raw = [];
                }
                parsed = raw.filter(isValidChangeExplanation);
            } catch {
                parsed = [{ change: 'Summary', reason: fullResponse }];
            }

            cache.current.set(cacheKey, parsed);
            setExplanations(parsed);
            setHasLoaded(true);
            if (parsed.length > 0) setExpandedIndex(0);

            trackTiming('ai-edit', 'explain_changes', startTime, { explanationCount: parsed.length });
        } catch (err) {
            if (requestManager.isAborted(err)) return;
            setError('Failed to load explanations. Please try again.');
            trackEvent('error', 'ai-edit', 'explain_changes_failed', {
                message: err instanceof Error ? err.message : String(err),
            });
        } finally {
            requestManager.endRequest();
            setIsLoading(false);
        }
    }, [original, suggested, cache, requestManager]);

    const handleHeaderClick = () => {
        if (!hasLoaded && !isSectionOpen) {
            // First open: fetch and expand
            fetchExplanations();
            setIsSectionOpen(true);
        } else {
            // Toggle collapse/expand
            setIsSectionOpen((v) => !v);
        }
    };

    return (
        <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
            {/* Header / Trigger */}
            <button
                onClick={handleHeaderClick}
                disabled={isLoading}
                className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors text-xs font-medium text-gray-600 disabled:opacity-50"
            >
                <span className="flex items-center gap-1.5">
                    <span>💡</span>
                    <span>{hasLoaded ? 'Change Explanations' : 'Explain Changes'}</span>
                </span>
                <span className="flex items-center gap-2">
                    {isLoading && (
                        <span className="animate-spin text-[10px]">⏳</span>
                    )}
                    {hasLoaded && !isLoading && (
                        <span className="text-[10px] text-gray-400">{explanations.length} change{explanations.length !== 1 ? 's' : ''}</span>
                    )}
                    <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ transform: isSectionOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}
                    >
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </span>
            </button>

            {isSectionOpen && (
                <>
                    {/* Error */}
                    {error && (
                        <div className="px-3 py-2 bg-red-50 text-xs text-red-600">
                            {error}
                            <button
                                onClick={fetchExplanations}
                                className="ml-2 underline hover:no-underline"
                            >
                                Retry
                            </button>
                        </div>
                    )}

                    {/* Loading skeleton */}
                    {isLoading && (
                        <div className="px-3 py-2 space-y-2">
                            {[1, 2, 3].map(i => (
                                <div key={i} className="animate-pulse flex flex-col gap-1">
                                    <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                                    <div className="h-2 bg-gray-100 rounded w-full"></div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Explanations accordion */}
                    {hasLoaded && !isLoading && explanations.length > 0 && (
                        <div className="divide-y divide-gray-100">
                            {explanations.map((exp, idx) => (
                                <div key={idx}>
                                    <button
                                        onClick={() => setExpandedIndex(expandedIndex === idx ? null : idx)}
                                        className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                                    >
                                        <span className="text-[10px] mt-0.5 text-gray-400 flex-shrink-0">
                                            {expandedIndex === idx ? '▼' : '▶'}
                                        </span>
                                        <span className="text-xs text-gray-700 font-medium line-clamp-1">
                                            {exp.change}
                                        </span>
                                    </button>
                                    {expandedIndex === idx && (
                                        <div className="px-3 pb-2 pl-7">
                                            <p className="text-xs text-gray-500 leading-relaxed">
                                                {exp.reason}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Empty state */}
                    {hasLoaded && !isLoading && explanations.length === 0 && (
                        <div className="px-3 py-2 text-xs text-gray-400 text-center">No changes found.</div>
                    )}
                </>
            )}
        </div>
    );
};

export default ExplainChangesView;
